import { getUpdatedTimestamp, randomInteger } from "@excalidraw/common";

import { isTextElement } from "./typeChecks";

import type { ElementsMap, ExcalidrawElement } from "./types";

/**
 * A named, coloured tag for canvas elements ("Context" in the UI). The
 * registry lives in `appState.sceneContexts`; the assignment lives in the
 * element's `customData.sceneContextId` and is only read / written through
 * the helpers below.
 */
export type SceneContext = Readonly<{
  id: string;
  name: string;
  color: string;
}>;

/** key of the "No context" row in `appState.hiddenSceneContextIds` */
export const NO_SCENE_CONTEXT_ID = "__none__";

/** swatches offered for contexts, cycled through for new ones */
export const SCENE_CONTEXT_COLORS = [
  "#e03131",
  "#1971c2",
  "#2f9e44",
  "#f08c00",
  "#9c36b5",
  "#0c8599",
  "#e8590c",
  "#5f3dc4",
] as const;

export const DEFAULT_SCENE_CONTEXT_COLOR = SCENE_CONTEXT_COLORS[0];

/** the colour used for "No context" (timestamps, rows) */
export const NO_SCENE_CONTEXT_COLOR = "#868e96";

export type HiddenSceneContextIds = Readonly<Record<string, true>>;

/** alpha multiplier of elements outside the focused context */
export const SCENE_CONTEXT_UNFOCUSED_OPACITY = 0.125;

const SCENE_CONTEXT_CUSTOM_DATA_KEY = "sceneContextId";

/** the element's own context id, `null` for "No context" */
export const getSceneContextId = (
  element: Pick<ExcalidrawElement, "customData">,
): string | null => {
  const id = element.customData?.[SCENE_CONTEXT_CUSTOM_DATA_KEY];
  return typeof id === "string" && id && id !== NO_SCENE_CONTEXT_ID ? id : null;
};

/**
 * The `customData` with the assignment set (or removed for `null`). Other
 * keys are kept; returns `undefined` when nothing is left.
 */
export const withSceneContextId = (
  element: Pick<ExcalidrawElement, "customData">,
  id: string | null,
): ExcalidrawElement["customData"] => {
  const { [SCENE_CONTEXT_CUSTOM_DATA_KEY]: _prev, ...rest } =
    element.customData ?? {};
  if (id && id !== NO_SCENE_CONTEXT_ID) {
    return { ...rest, [SCENE_CONTEXT_CUSTOM_DATA_KEY]: id };
  }
  return Object.keys(rest).length ? rest : undefined;
};

/** the visibility key of a context id (`NO_SCENE_CONTEXT_ID` for none) */
export const getSceneContextKey = (id: string | null) =>
  id ?? NO_SCENE_CONTEXT_ID;

/**
 * The context the element belongs to for visibility purposes: bound text
 * follows its container (when the container can be resolved).
 */
export const getEffectiveSceneContextId = (
  element: ExcalidrawElement,
  elementsMap?: ElementsMap,
): string | null => {
  if (elementsMap && isTextElement(element) && element.containerId) {
    const container = elementsMap.get(element.containerId);
    if (container) {
      return getSceneContextId(container);
    }
  }
  return getSceneContextId(element);
};

export const hasHiddenSceneContexts = (
  hiddenIds: HiddenSceneContextIds | null | undefined,
): hiddenIds is HiddenSceneContextIds => {
  if (!hiddenIds) {
    return false;
  }
  for (const _ in hiddenIds) {
    return true;
  }
  return false;
};

/** the one predicate deciding whether an element is hidden by its context */
export const isElementHiddenBySceneContext = (
  element: ExcalidrawElement,
  hiddenIds: HiddenSceneContextIds | null | undefined,
  elementsMap?: ElementsMap,
): boolean => {
  if (!hasHiddenSceneContexts(hiddenIds)) {
    return false;
  }
  return (
    hiddenIds[
      getSceneContextKey(getEffectiveSceneContextId(element, elementsMap))
    ] === true
  );
};

/**
 * The visibility key of the context in focus — the one new elements are
 * assigned to right now ("No context" while context mode is off) — or `null`
 * when focus is off.
 */
export const getFocusedSceneContextKey = (appState: {
  sceneContextFocusEnabled: boolean;
  sceneContextModeEnabled: boolean;
  activeSceneContextId: string | null;
}): string | null =>
  appState.sceneContextFocusEnabled
    ? getSceneContextKey(
        appState.sceneContextModeEnabled ? appState.activeSceneContextId : null,
      )
    : null;

/** whether focus is on and the element belongs to another context (dimmed) */
export const isElementOutsideFocusedSceneContext = (
  element: ExcalidrawElement,
  focusedKey: string | null | undefined,
  elementsMap?: ElementsMap,
): boolean =>
  !!focusedKey &&
  getSceneContextKey(getEffectiveSceneContextId(element, elementsMap)) !==
    focusedKey;

/**
 * The elements not hidden by their context. Returns the same array when no
 * context is hidden, so callers can memoize on identity.
 */
export const filterSceneContextVisibleElements = <T extends ExcalidrawElement>(
  elements: readonly T[],
  hiddenIds: HiddenSceneContextIds | null | undefined,
  elementsMap?: ElementsMap,
): readonly T[] => {
  if (!hasHiddenSceneContexts(hiddenIds)) {
    return elements;
  }
  return elements.filter(
    (element) =>
      !isElementHiddenBySceneContext(element, hiddenIds, elementsMap),
  );
};

/**
 * A copy of the element with the assignment changed (version bumped), or the
 * same element when it is already assigned to `id`.
 */
export const newElementWithSceneContextId = <T extends ExcalidrawElement>(
  element: T,
  id: string | null,
): T => {
  if (getSceneContextId(element) === id) {
    return element;
  }
  const customData = withSceneContextId(element, id);
  const { customData: _prev, ...rest } = element;
  return {
    ...rest,
    ...(customData ? { customData } : {}),
    version: element.version + 1,
    versionNonce: randomInteger(),
    updated: getUpdatedTimestamp(),
  } as T;
};

/**
 * Sets the assignment in place, for elements that are about to be inserted
 * into the scene (and so aren't tracked by anything yet). Doesn't bump the
 * version.
 */
export const assignSceneContextIdToNewElement = <T extends ExcalidrawElement>(
  element: T,
  id: string | null,
): T => {
  if (getSceneContextId(element) === id) {
    return element;
  }
  const customData = withSceneContextId(element, id);
  if (customData) {
    (element as { customData?: ExcalidrawElement["customData"] }).customData =
      customData;
  } else {
    delete (element as { customData?: ExcalidrawElement["customData"] })
      .customData;
  }
  return element;
};

/**
 * Context ids used by the elements but missing from the registry ("Unknown
 * context" rows), in first-seen order.
 */
export const getUnknownSceneContextIds = (
  elements: readonly ExcalidrawElement[],
  sceneContexts: readonly SceneContext[],
): string[] => {
  const known = new Set(sceneContexts.map((context) => context.id));
  const unknown = new Set<string>();
  for (const element of elements) {
    if (element.isDeleted) {
      continue;
    }
    const id = getSceneContextId(element);
    if (id && !known.has(id)) {
      unknown.add(id);
    }
  }
  return [...unknown];
};

/** element counts per visibility key (bound text not counted) */
export const getSceneContextElementCounts = (
  elements: readonly ExcalidrawElement[],
): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const element of elements) {
    if (element.isDeleted || (isTextElement(element) && element.containerId)) {
      continue;
    }
    const key = getSceneContextKey(getSceneContextId(element));
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
};

/**
 * Every visibility key a viewer can toggle: the registry, the unknown ids
 * used by the scene, and "No context".
 */
export const getSceneContextVisibilityKeys = (
  elements: readonly ExcalidrawElement[],
  sceneContexts: readonly SceneContext[],
): string[] => [
  ...sceneContexts.map((context) => context.id),
  ...getUnknownSceneContextIds(elements, sceneContexts),
  NO_SCENE_CONTEXT_ID,
];

/**
 * Next `hiddenSceneContextIds` after toggling a row's eye. With `solo`, every
 * other row is hidden — or, when `key` already is the only visible row,
 * everything is shown again.
 */
export const toggleSceneContextVisibility = (
  hiddenIds: HiddenSceneContextIds,
  key: string,
  allKeys: readonly string[],
  solo = false,
): HiddenSceneContextIds => {
  if (solo) {
    const isOnlyVisible =
      !hiddenIds[key] &&
      allKeys.every((other) => other === key || hiddenIds[other]);
    if (isOnlyVisible) {
      return {};
    }
    const next: Record<string, true> = {};
    for (const other of allKeys) {
      if (other !== key) {
        next[other] = true;
      }
    }
    return next;
  }
  const next: Record<string, true> = { ...hiddenIds };
  if (next[key]) {
    delete next[key];
  } else {
    next[key] = true;
  }
  return next;
};
